/**
 * RendererRegistry — the extensibility seam that replaces switch statements.
 *
 * Every `FloorObject.type` resolves to a `RendererEntry` (draw fn + hit
 * shape + default style). Built-ins cover the generic geometry types plus
 * semantic aliases (table/chair/wall/…) that are just presets over the same
 * primitives. CUSTOM types register at runtime — e.g. the POS binding
 * registers its own `table` renderer that maps `metadata.status` to colors,
 * keeping the ENGINE blind to domain meaning while the registry carries it.
 *
 * @module floorplan/renderer/RendererRegistry
 * @category FloorPlan
 */

import { FLOOR_OBJECT_TYPE } from '../models/object';
import type { ResolveHitShape } from '../utils/hitTest';
import {
  drawChair,
  drawCounter,
  drawDoor,
  drawEllipse,
  drawPolygonLike,
  drawRectLike,
  drawTextObject,
  drawWindow,
} from './renderers/draw';
import type { RendererEntry } from './types';

const registry = new Map<string, RendererEntry>();

// Const-object holder (not a module-level `let`) per the app-wide lint.
const registryState = { builtInsRegistered: false };

/** Register (or override) a renderer for an object type. */
export const registerRenderer = (type: string, entry: RendererEntry): void => {
  registry.set(type, entry);
};

/** Remove a custom renderer (falls back to built-ins / fallback entry). */
export const unregisterRenderer = (type: string): void => {
  registry.delete(type);
};

/** Generic rect fallback so an unknown type is never invisible. */
const FALLBACK_ENTRY: RendererEntry = {
  draw: (canvas, object, ctx) => {
    drawRectLike(canvas, object, ctx, {});
  },
  hitShape: 'rect',
};

/** Resolve the renderer for a type (auto-registers built-ins on first use). */
export const resolveRenderer = (type: string): RendererEntry => {
  if (!registryState.builtInsRegistered) registerBuiltInRenderers();
  const entry = registry.get(type);
  if (entry !== undefined) return entry;
  return FALLBACK_ENTRY;
};

/** Registry-aware hit-shape resolver — plug into `hitTestTopmost`. */
export const resolveRegistryHitShape: ResolveHitShape = (object) =>
  resolveRenderer(object.type).hitShape;

/**
 * Built-in renderers. Idempotent; `resolveRenderer` lazily invokes it so
 * consumers never need an explicit init call. Custom registrations are
 * never clobbered (built-ins only fill vacant keys).
 */
export const registerBuiltInRenderers = (): void => {
  registryState.builtInsRegistered = true;
  const setIfVacant = (type: string, entry: RendererEntry): void => {
    if (!registry.has(type)) registry.set(type, entry);
  };

  // ── Generic geometry ──────────────────────────────────────────────────
  setIfVacant(FLOOR_OBJECT_TYPE.RECT, {
    draw: (canvas, object, ctx) => {
      drawRectLike(canvas, object, ctx, {});
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.CIRCLE, {
    draw: (canvas, object, ctx) => {
      drawEllipse(canvas, object, ctx, {});
    },
    hitShape: 'ellipse',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.POLYGON, {
    draw: (canvas, object, ctx) => {
      drawPolygonLike(canvas, object, ctx, { close: true }, {});
    },
    hitShape: 'rect', // AABB approximation; exact polygon test is a follow-up
  });
  setIfVacant(FLOOR_OBJECT_TYPE.LINE, {
    draw: (canvas, object, ctx) => {
      drawPolygonLike(
        canvas,
        object,
        ctx,
        { close: false },
        {
          strokeWidth: 2,
        },
      );
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.TEXT, {
    draw: drawTextObject,
    hitShape: 'rect',
  });
  // Image + icon render as placeholder frames until the asset pipeline
  // lands (M10) — a visible frame beats an invisible object.
  setIfVacant(FLOOR_OBJECT_TYPE.IMAGE, {
    draw: (canvas, object, ctx) => {
      drawRectLike(canvas, object, ctx, { cornerRadius: 4 });
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.ICON, {
    draw: (canvas, object, ctx) => {
      drawEllipse(canvas, object, ctx, {});
    },
    hitShape: 'ellipse',
  });
  // Group nodes are logical containers — children render themselves.
  setIfVacant(FLOOR_OBJECT_TYPE.GROUP, {
    draw: () => undefined,
    hitShape: 'rect',
  });

  // ── Semantic aliases (presets over the same primitives) ──────────────
  setIfVacant(FLOOR_OBJECT_TYPE.TABLE, {
    draw: (canvas, object, ctx) => {
      drawRectLike(canvas, object, ctx, { cornerRadius: 8 });
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.CHAIR, {
    draw: (canvas, object, ctx) => {
      drawChair(canvas, object, ctx);
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.WALL, {
    draw: (canvas, object, ctx) => {
      drawRectLike(canvas, object, ctx, {
        fill: ctx.theme.wall,
        stroke: ctx.theme.wall,
        showLabel: false,
      });
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.COUNTER, {
    draw: (canvas, object, ctx) => {
      drawCounter(canvas, object, ctx);
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.ROOM, {
    draw: (canvas, object, ctx) => {
      // Dashed boundary — the architectural zone notation.
      drawRectLike(canvas, object, ctx, {
        filled: false,
        strokeWidth: 2,
        dashed: true,
      });
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.DOOR, {
    draw: (canvas, object, ctx) => {
      drawDoor(canvas, object, ctx);
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.WINDOW, {
    draw: (canvas, object, ctx) => {
      drawWindow(canvas, object, ctx);
    },
    hitShape: 'rect',
  });
  setIfVacant(FLOOR_OBJECT_TYPE.SHAPE, {
    draw: (canvas, object, ctx) => {
      drawRectLike(canvas, object, ctx, {});
    },
    hitShape: 'rect',
  });
};
